/**
 * Card encoding and trick-rank utilities for the Armenian Belot engine.
 *
 * **Card id:** integer `0..31` — `suit * 8 + rank` (stable for the whole engine; do not rename).
 *
 * @module game/belot/armenian/types
 */

/** Clubs ♣ */
const SUIT_CLUBS = 0;
/** Diamonds ♦ */
const SUIT_DIAMONDS = 1;
/** Hearts ♥ */
const SUIT_HEARTS = 2;
/** Spades ♠ */
const SUIT_SPADES = 3;

const SUIT_MIN = 0;
const SUIT_MAX = 3;

/** Seven */
const RANK_7 = 0;
const RANK_8 = 1;
/** Nine */
const RANK_9 = 2;
/** Jack */
const RANK_J = 3;
/** Queen */
const RANK_Q = 4;
/** King */
const RANK_K = 5;
/** Ten */
const RANK_10 = 6;
/** Ace */
const RANK_A = 7;

const RANK_MIN = 0;
const RANK_MAX = 7;

const CARDS_PER_DECK = 32;

/**
 * Plain-suit trick strength: 7 weakest … A strongest (standard 32-card ordering).
 * Index = rank constant (`RANK_*`).
 * @readonly
 */
const TRICK_STRENGTH_PLAIN = Object.freeze(
  /** @type {readonly number[]} */ ([0, 1, 2, 3, 4, 5, 6, 7]),
);

/**
 * Trump trick strength for Belot family: J > 9 > A > 10 > K > Q > 8 > 7.
 * Index = rank constant (`RANK_*`).
 * @readonly
 */
const TRICK_STRENGTH_TRUMP = Object.freeze(
  /** @type {readonly number[]} */ ([0, 1, 6, 7, 2, 3, 4, 5]),
);

/**
 * @param {number} suit
 * @param {number} rank
 * @returns {number}
 */
function makeCard(suit, rank) {
  return suit * 8 + rank;
}

/**
 * @param {number} card
 * @returns {number} `SUIT_*`
 */
function cardSuit(card) {
  return Math.floor(card / 8);
}

/**
 * @param {number} card
 * @returns {number} `RANK_*`
 */
function cardRank(card) {
  return card % 8;
}

/**
 * @param {number} card
 * @returns {boolean}
 */
function isValidCard(card) {
  return (
    Number.isInteger(card) &&
    card >= 0 &&
    card < CARDS_PER_DECK
  );
}

/**
 * @param {number} seat `0..3`
 * @returns {number} partner seat
 */
function partnerSeat(seat) {
  return (seat + 2) % 4;
}

/**
 * Seats 0 & 2 → team 0; seats 1 & 3 → team 1.
 * @param {number} seat `0..3`
 * @returns {0|1}
 */
function teamOfSeat(seat) {
  return /** @type {0|1} */ (seat % 2);
}

/**
 * @param {number} rank
 * @param {boolean} isTrumpCard
 * @returns {number}
 */
function trickStrengthForRank(rank, isTrumpCard) {
  const table = isTrumpCard ? TRICK_STRENGTH_TRUMP : TRICK_STRENGTH_PLAIN;
  return table[rank] ?? -1;
}

/**
 * Dominance for trick resolution: offsuit discard < lead suit < trump.
 * @param {number} cardSuit_
 * @param {number} leadSuit
 * @param {number|null} trumpSuit `null` = no-trump contract (no trump suit).
 * @returns {0|1|2}
 */
function trickSuitTier(cardSuit_, leadSuit, trumpSuit) {
  if (trumpSuit === "all_trump") {
    return cardSuit_ === leadSuit ? 1 : 0;
  }
  if (trumpSuit != null && cardSuit_ === trumpSuit) return 2;
  if (cardSuit_ === leadSuit) return 1;
  return 0;
}

/**
 * Sort key for finding trick winner: higher wins. Only meaningful among cards
 * that participated in the trick; discards get tier 0 and lose to any lead/trump.
 *
 * @param {number} card
 * @param {number} leadSuit
 * @param {number|null} trumpSuit
 * @returns {{ tier: number, strength: number }}
 */
function trickCompareKey(card, leadSuit, trumpSuit) {
  const s = cardSuit(card);
  const r = cardRank(card);
  const tier = trickSuitTier(s, leadSuit, trumpSuit);
  const isTrumpCard =
    trumpSuit === "all_trump" ? tier > 0 : trumpSuit != null && s === trumpSuit;
  const strength = tier === 0 ? -1 : trickStrengthForRank(r, isTrumpCard);
  return { tier, strength };
}

/**
 * @param {number} a
 * @param {number} b
 * @param {number} leadSuit
 * @param {number|null} trumpSuit
 * @returns {number} negative if a loses to b, positive if a wins, 0 tie
 */
function compareCardsForTrick(a, b, leadSuit, trumpSuit) {
  const ka = trickCompareKey(a, leadSuit, trumpSuit);
  const kb = trickCompareKey(b, leadSuit, trumpSuit);
  if (ka.tier !== kb.tier) return ka.tier - kb.tier;
  return ka.strength - kb.strength;
}

module.exports = {
  SUIT_CLUBS,
  SUIT_DIAMONDS,
  SUIT_HEARTS,
  SUIT_SPADES,
  SUIT_MIN,
  SUIT_MAX,
  RANK_7,
  RANK_8,
  RANK_9,
  RANK_J,
  RANK_Q,
  RANK_K,
  RANK_10,
  RANK_A,
  RANK_MIN,
  RANK_MAX,
  CARDS_PER_DECK,
  TRICK_STRENGTH_PLAIN,
  TRICK_STRENGTH_TRUMP,
  makeCard,
  cardSuit,
  cardRank,
  isValidCard,
  partnerSeat,
  teamOfSeat,
  trickStrengthForRank,
  trickSuitTier,
  trickCompareKey,
  compareCardsForTrick,
};
